import { useState, useMemo } from 'react';
import { X, Clock, Download, RotateCcw, Trash2, Calendar, FileText, Info, GitCompare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { HistorySnapshot, FileHistory } from '@/hooks/use-local-history';
import { formatFileSize, formatRelativeTime } from '@/hooks/use-local-history';
import { DiffViewer } from './diff-viewer';

interface LocalHistoryPanelProps {
  filePath: string;
  fileHistory: FileHistory | undefined;
  currentContent?: string;
  onClose: () => void;
  onRestore: (snapshot: HistorySnapshot) => void;
  onDelete: (snapshotId: string) => void;
}

export function LocalHistoryPanel({
  filePath,
  fileHistory,
  currentContent,
  onClose,
  onRestore,
  onDelete
}: LocalHistoryPanelProps) {
  const [selectedSnapshot, setSelectedSnapshot] = useState<HistorySnapshot | null>(null);
  const [compareSnapshot, setCompareSnapshot] = useState<HistorySnapshot | null>(null);
  const [showDiff, setShowDiff] = useState(false);

  const snapshots = useMemo(() => {
    return fileHistory?.snapshots || [];
  }, [fileHistory]);

  const getTriggerIcon = (trigger: HistorySnapshot['trigger']) => {
    switch (trigger) {
      case 'user_edit':
        return <FileText className="size-4 text-blue-500" />;
      case 'ai_generation_start':
        return <Clock className="size-4 text-purple-500" />;
      case 'ai_generation_complete':
        return <Clock className="size-4 text-green-500" />;
      case 'file_save':
        return <Download className="size-4 text-orange-500" />;
      case 'file_delete':
        return <Trash2 className="size-4 text-red-500" />;
      case 'manual':
        return <Info className="size-4 text-gray-500" />;
      default:
        return <Clock className="size-4 text-gray-500" />;
    }
  };

  const getTriggerColor = (trigger: HistorySnapshot['trigger']) => {
    switch (trigger) {
      case 'user_edit':
        return 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800';
      case 'ai_generation_start':
        return 'bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800';
      case 'ai_generation_complete':
        return 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800';
      case 'file_save':
        return 'bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800';
      case 'file_delete':
        return 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800';
      case 'manual':
        return 'bg-gray-50 dark:bg-gray-950 border-gray-200 dark:border-gray-800';
      default:
        return 'bg-gray-50 dark:bg-gray-950 border-gray-200 dark:border-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Clock className="size-5 text-gray-500" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Local History
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {filePath}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="size-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Timeline List */}
          <div className="w-1/3 border-r border-gray-200 dark:border-gray-700">
            <ScrollArea className="h-full">
              <div className="p-4 space-y-2">
                {snapshots.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Clock className="size-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No history available</p>
                  </div>
                ) : (
                  snapshots.map((snapshot) => (
                    <button
                      key={snapshot.id}
                      onClick={() => setSelectedSnapshot(snapshot)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedSnapshot?.id === snapshot.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950'
                          : getTriggerColor(snapshot.trigger)
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {getTriggerIcon(snapshot.trigger)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {snapshot.description}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="size-3" />
                              {formatRelativeTime(snapshot.timestamp)}
                            </span>
                            <span>•</span>
                            <span>{formatFileSize(snapshot.size)}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Preview & Actions */}
          <div className="flex-1 flex flex-col">
            {selectedSnapshot ? (
              <>
                {/* Snapshot Info */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {selectedSnapshot.description}
                    </h3>
                    <div className="flex items-center gap-2">
                      {currentContent && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCompareSnapshot(selectedSnapshot);
                            setShowDiff(true);
                          }}
                          className="text-xs"
                        >
                          <GitCompare className="size-3 mr-1" />
                          Compare with Current
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRestore(selectedSnapshot)}
                        className="text-xs"
                      >
                        <RotateCcw className="size-3 mr-1" />
                        Restore
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(selectedSnapshot.id)}
                        className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="size-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="size-3" />
                      {new Date(selectedSnapshot.timestamp).toLocaleString()}
                    </span>
                    <span>•</span>
                    <span>{formatFileSize(selectedSnapshot.size)}</span>
                    <span>•</span>
                    <span className="capitalize">{selectedSnapshot.trigger.replace('_', ' ')}</span>
                  </div>
                </div>

                {/* Content Preview */}
                <ScrollArea className="flex-1">
                  <div className="p-4">
                    <pre className="text-xs font-mono bg-gray-50 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto">
                      <code className="text-gray-900 dark:text-gray-100">
                        {selectedSnapshot.content}
                      </code>
                    </pre>
                  </div>
                </ScrollArea>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
                <div className="text-center">
                  <FileText className="size-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Select a snapshot to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Stats */}
        {fileHistory && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
              <span>
                {snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''} available
              </span>
              <span>
                Last modified: {formatRelativeTime(fileHistory.lastModified)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Diff Viewer Modal */}
      {showDiff && compareSnapshot && currentContent && (
        <DiffViewer
          filePath={filePath}
          originalContent={compareSnapshot.content}
          modifiedContent={currentContent}
          originalLabel={`${compareSnapshot.description} (${new Date(compareSnapshot.timestamp).toLocaleString()})`}
          modifiedLabel="Current Version"
          onClose={() => {
            setShowDiff(false);
            setCompareSnapshot(null);
          }}
          onRevert={() => {
            onRestore(compareSnapshot);
            setShowDiff(false);
            setCompareSnapshot(null);
            onClose();
          }}
        />
      )}
    </div>
  );
}

